import { JSX, useState } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';

import { Select } from 'components/Select';
import FormInputWrapper from 'components/input/FormInputWrapper';
import { useLoadContext } from 'hooks/useLoadContext';
import { NormalizedLoad } from 'types/Load';
import { getFieldAttribute } from 'types/LoadAttributes';
import { Undef } from 'types/UtilityTypes';

import { LoadBuildingTextInput } from './LoadBuildingTextInput';

// The rate data field each kind of fuel surcharge is stored in.
export enum FuelSurchargeType {
  Percent = 'fscPercent',
  PerMile = 'fscPerMile',
  Flat = 'fscFlatRate',
}

export type FuelSurchargeTypeOption = {
  value: FuelSurchargeType;
  label: string;
};

interface FuelSurchargeFieldsProps {
  formMethods: UseFormReturn<NormalizedLoad>;
  // The surcharge types the TMS can take, first one is the default.
  typeOptions: FuelSurchargeTypeOption[];
}

// One fuel surcharge for the customer rate: a type and its amount. A load states one surcharge,
// so switching the type moves the amount and clears the other types.
export function FuelSurchargeFields({
  formMethods,
  typeOptions,
}: FuelSurchargeFieldsProps): JSX.Element {
  const { control, getValues, setValue } = formMethods;
  const { fieldAttributes } = useLoadContext();

  const [percent, perMile, flat] = useWatch({
    control,
    name: [
      `rateData.${FuelSurchargeType.Percent}`,
      `rateData.${FuelSurchargeType.PerMile}`,
      `rateData.${FuelSurchargeType.Flat}`,
    ],
  });
  const [pickedType, setPickedType] = useState<Undef<FuelSurchargeType>>();

  // The type with an amount wins, so a suggestion or a loaded load shows its own surcharge.
  const typeWithAmount = typeOptions.find(({ value }) =>
    hasAmount(
      {
        [FuelSurchargeType.Percent]: percent,
        [FuelSurchargeType.PerMile]: perMile,
        [FuelSurchargeType.Flat]: flat,
      }[value]
    )
  )?.value;
  const selectedType = typeWithAmount ?? pickedType ?? typeOptions[0]?.value;

  const isReadOnly = Boolean(
    getFieldAttribute(fieldAttributes, `rateData.${selectedType}`)?.isReadOnly
  );

  const handleTypeChange = (next: FuelSurchargeType) => {
    const amount = getValues(`rateData.${selectedType}`);
    typeOptions.forEach(({ value }) => {
      setValue(`rateData.${value}`, value === next ? amount : null, {
        shouldDirty: true,
      });
    });
    setPickedType(next);
  };

  return (
    <>
      <div className='col-span-2'>
        <FormInputWrapper
          name='rateData.fscType'
          label='Fuel Surcharge Type'
          hideAIHint
        >
          <Select
            name='rateData.fscType'
            // FormInputWrapper already spaces the label like the amount input beside it.
            className='h-8 mt-0'
            placeholder='Choose'
            disabled={isReadOnly}
            value={selectedType}
            onChange={(value) => handleTypeChange(value as FuelSurchargeType)}
            options={typeOptions}
          />
        </FormInputWrapper>
      </div>
      <div className='col-span-2'>
        <LoadBuildingTextInput
          key={selectedType}
          name={`rateData.${selectedType}`}
          label={
            typeOptions.find(({ value }) => value === selectedType)?.label ??
            'Fuel Surcharge'
          }
          inputType='number'
          step={0.01}
          options={{ valueAsNumber: true, min: 0 }}
          required={false}
        />
      </div>
    </>
  );
}

function hasAmount(value: unknown): boolean {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}
